// src/services/invoiceService.js
import apiService from "./apiService"; // Import the reusable API service

const INVOICE_API_URL = "/invoices"; // Base endpoint for invoice-related operations

/**
 * Fetch all invoices with optional pagination.
 * @param {number} page - The page number to fetch.
 * @param {number} limit - The number of invoices per page.
 * @returns {Array} - The array of invoice objects.
 * @throws Will throw an error if the API request fails or returns unexpected data.
 */
export const getAllInvoices = async (page = 1, limit = 10) => {
  try {
    const response = await apiService.get(`${INVOICE_API_URL}/all-invoices`, {
      params: { page, limit },
    });

    // Check if the response structure is valid
    if (response?.data?.success && Array.isArray(response.data.invoices)) {
      return response.data.invoices; // Return the array of invoices
    } else {
      throw new Error("Invalid API response structure");
    }
  } catch (error) {
    console.error("Error fetching invoices:", error.message);
    throw new Error("Failed to fetch invoices. Please try again later.");
  }
};

/**
 * Fetch a single invoice by its ID.
 * @param {string} invoiceId - The ID of the invoice to fetch.
 * @returns {Object} - The invoice object.
 * @throws Will throw an error if the API request fails or returns unexpected data.
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    const response = await apiService.get(
      `${INVOICE_API_URL}/invoice/${invoiceId}`
    );

    // Check if the response structure is valid
    if (response?.data?.success && response.data.invoice) {
      return response.data.invoice; // Return the invoice object
    } else {
      throw new Error("Invalid API response structure");
    }
  } catch (error) {
    console.error("Error fetching invoice:", error.message);
    throw new Error("Failed to fetch invoice. Please try again later.");
  }
};

/**
 * Generate a new invoice for a specific customer.
 * @param {string} customerId - The ID of the customer.
 * @returns {Object} - The generated invoice object.
 * @throws Will throw an error if the API request fails.
 */
export const generateInvoice = async (customerId, month) => {
  try {
    const response = await apiService.post(
      `${INVOICE_API_URL}/generate-invoice`,
      {
        customer_id: customerId,
        month: month,
      }
    );

    if (response?.data?.success) {
      return response.data; // Return the generated invoice data
    } else {
      throw new Error("Invoice generation failed");
    }
  } catch (error) {
    console.error("Error generating invoice:", error.message);
    throw new Error("Failed to generate invoice. note: \n" + error.message);
  }
};

/**
 * Generate invoices for all customers.
 * @returns {Object} - The response containing invoice generation status.
 * @throws Will throw an error if the API request fails.
 */
export const generateAllInvoices = async (month) => {
  try {
    const response = await apiService.post(
      `${INVOICE_API_URL}/generate-all-invoices`,
      {
        month: month,
      }
    );

    if (response?.data?.success) {
      return response.data; // Return success response
    } else {
      throw new Error("Bulk invoice generation failed");
    }
  } catch (error) {
    console.error("Error generating all invoices:", error.message);
    throw new Error("Failed to generate all invoices. Please try again later.");
  }
};

/**
 * Generate invoices for all customers.
 * @returns {Object} - The response containing invoice generation status.
 * @throws Will throw an error if the API request fails.
 */
export const generateAllInvoicesInternal = async (month) => {
  try {
    const response = await apiService.post(
      `${INVOICE_API_URL}/generate-invoices-async`,
      {
        month: month,
      }
    );

    if (response?.data?.success) {
      return response.data; // Return success response
    } else {
      throw new Error("Bulk invoice generation failed");
    }
  } catch (error) {
    console.error("Error generating all invoices:", error.message);
    throw new Error("Failed to generate all invoices. Please try again later.");
  }
};
/**
 * Generate customer backup PDF.
 * @returns {Object} - Response containing PDF URL and status.
 * @throws Will throw an error if the API request fails.
 */
export const generateCustomerBackupPdf = async () => {
  try {
    const response = await apiService.get(`${INVOICE_API_URL}/generate-customer-backup`);

    if (response?.data?.success && response.data.url) {
      return response.data; // ✅ { success: true, url: "https://..." }
    } else {
      throw new Error("Customer backup generation failed.");
    }
  } catch (error) {
    console.error("❌ Error generating customer backup PDF:", error.message);
    throw new Error("Failed to generate customer backup. Please try again later.");
  }
};
